import { useMemo } from 'react'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'
import { UserRoundX } from 'lucide-react'

import Avatar from '@/elements/Avatar'
import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import { defaultColumnOptions, type TableAny } from '@/utils/tables'
import type { AssignedSubjectResponse } from '@/api/types/Subject'

interface MemberIndividualsTableType {
  id: AssignedSubjectResponse['id']
  avatarImg?: string
  name: AssignedSubjectResponse['user']['details']['fullName']
}

const columnHelper = createColumnHelper<MemberIndividualsTableType>()

export function useMemberIndividualsTable(
  individuals: AssignedSubjectResponse[],
  onRemoveIndividual: (id: string) => void
) {

  const data: MemberIndividualsTableType[] = useMemo(() => (
    individuals ? individuals.map(row => ({
      id: row?.id,
      avatarImg: row?.user?.assets?.PROFILE_PICTURE || '',
      name: row?.user?.details?.fullName || '',
    })) : []
  ), [individuals])

  const columns: ColumnDef<MemberIndividualsTableType, TableAny>[] = [
    columnHelper.accessor('name', {
      ...defaultColumnOptions,
      header: 'Name',
      enableSorting: false,
      cell: info => (
        <Flex inline align='center' gap={2} className='user-cell'>
          <Avatar
            name={info.getValue() as string}
            src={info.row.original.avatarImg}
            size='extraSmall'
            color='gray'
          />
          <Text variant='textblock'>{info.getValue() as string}</Text>
        </Flex>
      )
    }),
    columnHelper.display({
      id: 'actions',
      ...defaultColumnOptions(),
      header: 'Actions',
      meta: { width: 100 },
      enableSorting: false,
      cell: info => {
        const individualId = info.row.original.id
        return (
          <Flex justify='center' align='center' className='actions-cell'>
            <Button
              small
              variant='iconDangerBtn'
              onClick={() => onRemoveIndividual(individualId)}
            >
              <UserRoundX size={18} />
            </Button>
          </Flex>
        )
      }
    }),
  ]

  return { data, columns }
}
